/**
 * Pure screenplay model helpers shared by the Script Editor and Script
 * Upload panels (Agents 01 + 02). No DOM, no store — just classification
 * and parsing so both the live editor and the ingest pipeline agree on
 * what a line *is*.
 *
 * The canonical scene content is `StudioScene.elements[]` (structured
 * Final-Draft elements). `body` is kept as a flat `\n`-joined mirror for
 * back-compat. These helpers migrate a freeform body into typed elements
 * and classify a single edited line in context.
 */

import type { ScriptElement, ScriptElementType, StudioCharacter } from "../StudioStore";

export type ElementType = ScriptElementType;

/** INT./EXT. scene-heading detector. */
export const SLUG_RE = /^\s*(INT\.?\/EXT\.?|EXT\.?\/INT\.?|INT|EXT|I\/E|EST)[.\s]/i;
/** Transition detector (CUT TO:, FADE OUT, … or any "… TO:" tail). */
export const TRANSITION_RE =
  /(^\s*(FADE (IN|OUT)|DISSOLVE( TO)?|SMASH CUT|MATCH CUT|CUT TO|JUMP CUT|FADE TO BLACK)\b)|(\bTO:\s*$)/i;
/** Wrapped parenthetical detector — a whole line in (parens). */
export const PAREN_RE = /^\s*\(.*\)\s*$/;

/** Find-or-not a character whose name matches (case-insensitive). */
export function findCharacterByName(
  name: string,
  characters: readonly StudioCharacter[],
): StudioCharacter | null {
  const key = name.trim().toLowerCase();
  if (!key) return null;
  return characters.find((c) => c.name.trim().toLowerCase() === key) ?? null;
}

/**
 * Classify a single line's element type from its text + the previous
 * line's type (Fountain-style context). `pinned` lets the editor keep a
 * user-chosen type (action/character/dialogue) and only re-derive the
 * unambiguous structural types (slug / transition / parenthetical).
 */
export function classifyType(
  raw: string,
  prevType: ElementType | null,
  isKnownCharacter: boolean,
  pinned?: ElementType | null,
): ElementType {
  const trimmed = raw.trim();
  if (SLUG_RE.test(trimmed)) return "slug";
  if (TRANSITION_RE.test(trimmed)) return "transition";
  if (PAREN_RE.test(trimmed)) return "parenthetical";

  // A user-pinned non-structural type wins over heuristics.
  if (pinned && (pinned === "action" || pinned === "character" || pinned === "dialogue")) {
    return pinned;
  }

  if (isKnownCharacter) return "character";

  const looksLikeCue =
    trimmed.length > 0 &&
    trimmed.length <= 38 &&
    trimmed === trimmed.toUpperCase() &&
    /[A-Z]/.test(trimmed) &&
    !/[.!?,]$/.test(trimmed);
  if (looksLikeCue) return "character";

  if (
    trimmed.length > 0 &&
    (prevType === "character" || prevType === "parenthetical" || prevType === "dialogue")
  ) {
    return "dialogue";
  }
  return "action";
}

/** Parse a slug line into a location name + INT/EXT + time-of-day. */
export function parseSlug(text: string): {
  locationName: string;
  intExt: "INT" | "EXT" | "INT/EXT" | null;
  timeOfDay: string | null;
} | null {
  const trimmed = text.trim();
  if (!SLUG_RE.test(trimmed)) return null;
  const prefixMatch = /^\s*(INT\.?\/EXT\.?|EXT\.?\/INT\.?|INT|EXT|I\/E|EST)\b\.?/i.exec(trimmed);
  let intExt: "INT" | "EXT" | "INT/EXT" | null = null;
  if (prefixMatch) {
    const p = prefixMatch[1].toUpperCase().replace(/\./g, "");
    if (p.includes("/") || p === "IE") intExt = "INT/EXT";
    else if (p.startsWith("INT")) intExt = "INT";
    else if (p.startsWith("EXT") || p === "EST") intExt = "EXT";
  }
  let rest = prefixMatch ? trimmed.slice(prefixMatch[0].length) : trimmed;
  rest = rest.replace(/^[\s.]+/, "");
  // Time-of-day is the tail after the last " - " / " — " separator.
  let timeOfDay: string | null = null;
  const sepMatch = /\s[-–—]\s*([^-–—]+)$/.exec(rest);
  if (sepMatch) {
    timeOfDay = sepMatch[1].trim() || null;
    rest = rest.slice(0, sepMatch.index);
  }
  const locationName = rest.trim().replace(/[.\s]+$/, "");
  if (!locationName) return null;
  return { locationName, intExt, timeOfDay };
}

/**
 * Extract `@`-reference tokens from a line, resolving each to the
 * longest matching known entity name when possible. Unresolved tokens
 * are returned with `entityName` = the captured word so the editor can
 * offer "create new".
 */
export interface RefTokenMatch {
  /** The captured `@…` substring as it appears in the text. */
  raw: string;
  /** Resolved or captured entity name. */
  entityName: string;
  /** Start/end character offsets of the `@…` token in the line. */
  start: number;
  end: number;
}

export function extractRefTokens(
  text: string,
  knownNames: readonly string[],
): RefTokenMatch[] {
  const out: RefTokenMatch[] = [];
  const sorted = [...knownNames].sort((a, b) => b.length - a.length);
  let i = 0;
  while (i < text.length) {
    if (text[i] !== "@") {
      i += 1;
      continue;
    }
    const after = text.slice(i + 1);
    // Prefer the longest known entity name that follows the `@`.
    let matchedName: string | null = null;
    for (const name of sorted) {
      if (!name) continue;
      if (after.toLowerCase().startsWith(name.toLowerCase())) {
        const boundary = after[name.length];
        if (boundary === undefined || !/[A-Za-z0-9_'-]/.test(boundary)) {
          matchedName = name;
          break;
        }
      }
    }
    if (matchedName) {
      out.push({
        raw: `@${after.slice(0, matchedName.length)}`,
        entityName: matchedName,
        start: i,
        end: i + 1 + matchedName.length,
      });
      i += 1 + matchedName.length;
      continue;
    }
    const wordMatch = /^[A-Za-z0-9][A-Za-z0-9_'-]*/.exec(after);
    if (wordMatch) {
      out.push({
        raw: `@${wordMatch[0]}`,
        entityName: wordMatch[0],
        start: i,
        end: i + 1 + wordMatch[0].length,
      });
      i += 1 + wordMatch[0].length;
      continue;
    }
    i += 1;
  }
  return out;
}

/** The element type a fresh line gets when you press Enter after `type`. */
export function nextTypeOnEnter(type: ElementType): ElementType {
  switch (type) {
    case "slug":
      return "action";
    case "character":
      return "dialogue";
    case "parenthetical":
      return "dialogue";
    case "dialogue":
      return "action";
    case "transition":
      return "slug";
    default:
      return "action";
  }
}

/** The next type when the user cycles a line's type (Tab / chip click). */
const CYCLE_ORDER: ElementType[] = [
  "action",
  "character",
  "dialogue",
  "parenthetical",
  "slug",
  "transition",
];
export function cycleType(type: ElementType): ElementType {
  const idx = CYCLE_ORDER.indexOf(type);
  return CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
}

/** The three quick types surfaced on the per-line type control. */
export const QUICK_TYPES: ElementType[] = ["action", "character", "dialogue"];

export function placeholderFor(type: ElementType): string {
  switch (type) {
    case "slug":
      return "INT. LOCATION - DAY";
    case "character":
      return "CHARACTER";
    case "parenthetical":
      return "(beat)";
    case "dialogue":
      return "Dialogue…";
    case "transition":
      return "CUT TO:";
    default:
      return "Action…";
  }
}

/** Short label shown on the line-type chip. */
export function typeLabel(type: ElementType): string {
  switch (type) {
    case "slug":
      return "Scene";
    case "character":
      return "Cue";
    case "parenthetical":
      return "Paren";
    case "dialogue":
      return "Dialogue";
    case "transition":
      return "Trans";
    default:
      return "Action";
  }
}

let elementSeq = 0;
/** Generate a stable-enough element id for the editor's working model. */
export function makeElementId(): string {
  elementSeq += 1;
  return `el_${Date.now().toString(36)}${elementSeq.toString(36)}${Math.random()
    .toString(36)
    .slice(2, 5)}`;
}

/**
 * Migrate a scene's freeform `body` into typed structured elements,
 * resolving character ids from the known cast. Used when a scene has no
 * `elements[]` yet (legacy / freshly-typed state).
 */
export function bodyToElements(
  body: string,
  characters: readonly StudioCharacter[],
): ScriptElement[] {
  const rawLines = body.length === 0 ? [""] : body.split("\n");
  const out: ScriptElement[] = [];
  let prevType: ElementType | null = null;
  for (const raw of rawLines) {
    const known = findCharacterByName(raw.trim(), characters);
    const type = classifyType(raw, prevType, !!known);
    const characterId = type === "character" ? known?.id ?? null : null;
    out.push({ id: makeElementId(), type, text: raw, characterId });
    prevType = type;
  }
  return out;
}

/**
 * Resolve / refresh the structured elements for a scene: returns the
 * stored `elements[]` when present (re-resolving character ids against
 * the current cast), else migrates from `body`.
 */
export function resolveSceneElements(
  scene: { body: string; elements?: ScriptElement[] },
  characters: readonly StudioCharacter[],
): ScriptElement[] {
  if (scene.elements && scene.elements.length > 0) {
    return scene.elements.map((el) => {
      if (el.type === "character" || el.type === "dialogue") {
        const resolved =
          (el.characterId &&
            characters.some((c) => c.id === el.characterId) &&
            el.characterId) ||
          (el.type === "character"
            ? findCharacterByName(el.text, characters)?.id ?? null
            : null);
        return { ...el, characterId: resolved };
      }
      return { ...el, characterId: null };
    });
  }
  return bodyToElements(scene.body, characters);
}

/** Join elements back into a flat `body` mirror. */
export function elementsToBody(elements: readonly ScriptElement[]): string {
  return elements.map((el) => el.text).join("\n");
}
